import { z } from 'zod'
import { isSuccessResponse, ErrorCodes, type BaseError } from '@cplug/frontend-core/models'
import { Result } from '@cplug/frontend-core/utils'
import { BaseGatewayErrorDTO } from '@cplug/frontend-core/gateways'
import type { ERPSettings } from '../models/ERPSettings'
import { mapDTOToERPSettings, mapERPSettingsToDTO, ERPSettingsDTO } from './DTOs/ERPSettingsDTO'
import { CPBaseCRUDGateway } from '~/shared/gateways/CPBaseCRUDGateway'

type ListDTO = { data: ERPSettingsDTO[] }

const ERPSettingsWrapperDTO = z.object({ data: ERPSettingsDTO })

export class CPERPSettingsGateway extends CPBaseCRUDGateway<ERPSettingsDTO, ListDTO, ERPSettings> {
  protected apiUrlDomain = 'settings/erp'

  protected dtoToModelMapper = (dto: ERPSettingsDTO): Result<ERPSettings> => Result.ofValue(mapDTOToERPSettings(dto))

  protected modelToDtoMapper = mapERPSettingsToDTO
  protected dtoSchema = ERPSettingsDTO

  protected get listField (): 'data' {
    return 'data'
  }

  protected override get modelIdField (): keyof ERPSettings {
    return 'news'
  }

  async getSettings (): Promise<Result<ERPSettings>> {
    const response = await this.get(this.apiUrlDomain)

    if (response?.data == null || !isSuccessResponse(response)) {
      return Result.ofError(
        new BaseGatewayErrorDTO().deserialize(response?.data as BaseGatewayErrorDTO) ?? <BaseError>{ code: ErrorCodes.Unknown }
      )
    }

    return this.parseResponse(response, ERPSettingsWrapperDTO, this.mapDTOToERPSettingsWrapper.bind(this))
  }

  private mapDTOToERPSettingsWrapper (dto: z.infer<typeof ERPSettingsWrapperDTO>): ERPSettings {
    return mapDTOToERPSettings(dto.data)
  }

  async updateSettings (model: ERPSettings): Promise<Result<ERPSettings>> {
    const response = await this.patch(this.apiUrlDomain, this.modelToDtoMapper(model))

    if (response?.data == null || !isSuccessResponse(response)) {
      return Result.ofError(
        new BaseGatewayErrorDTO().deserialize(response?.data as BaseGatewayErrorDTO) ?? <BaseError>{ code: ErrorCodes.Unknown }
      )
    }

    return this.parseSingleResponse(response, this.dtoSchema, this.dtoToModelMapper)
  }
}
